import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, includes } from "lodash";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import CloseIcon from "../../Common/CloseIcon";

import { getLatestValue } from "../../../utils/event";

import colors from "../../../assets/style/colors";
import { fetchGet } from "../../../api/fetch";

const styles = {
  showRoi: {
    color: colors.primary,
    fontSize: "0.875em",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
};

const FocusPhoto = (props) => {
  const { classes, focusElement } = props;
  const [showRoi, setShowRoi] = React.useState(true);
  const [imgSize, setImgSize] = React.useState({ width: 0, height: 0 });
  const [scale, setScale] = React.useState(1);
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [squareSize, setSquareSize] = React.useState(350);

  const [image, setImage] = React.useState();

  React.useEffect(() => {
    const ac = new AbortController();
    setOffset({ x: 0, y: 0 });
    setZoom(1);
    setScale(1);
    setIsZoomed(false);

    downloadImage(ac);

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusElement.url]);

  const downloadImage = async (ac) => {
    if (focusElement.url) {
      try {
        let res = await fetchGet(getUrl(focusElement.url));
        if (!ac.signal.aborted) {
          setImage(res);
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  React.useEffect(() => {
    let canvas = document.getElementById("canvas");
    if (canvas) {
      let ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let img = new Image();
      img.onload = function () {
        setImgSize({ width: this.width, height: this.height });
        let scale =
          this.width > this.height
            ? squareSize / this.width
            : squareSize / this.height;
        scale = scale * zoom;
        setScale(scale);
        ctx.save();
        ctx.scale(scale, scale);
        ctx.translate(offset.x, offset.y);
        ctx.drawImage(img, 0, 0);
        ctx.strokeStyle = "#00E7FF";
        ctx.lineWidth = (isZoomed ? 3 : 2) / scale;
        if (showRoi && focusElement.roiOverlay) {
          focusElement.roiOverlay.forEach((r) => {
            ctx.strokeRect(
              r.x1 - 2,
              r.y1 - 2,
              r.x2 - r.x1 + 4,
              r.y2 - r.y1 + 4
            );
          });
        }
        ctx.restore();
      };
      img.src = image;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRoi, zoom, offset, isZoomed, image]);

  const getUrl = (url) => {
    let res = "";
    if (props.local) {
      res = url;
    } else if (includes(url, "/api/images/")) {
      let index = url.indexOf("/api/images/") + 12;
      url = url.slice(index);
      res = `images/${url}`;
    } else {
      res = `images/${url}`;
    }
    return res;
  };

  const onZoom = (event) => {
    setIsZoomed(true);
    setSquareSize(850);
    /*let canvasContainer = document
      .getElementById("canvasContainer")
      .getBoundingClientRect();
    let clickPositionOnCanvasX = event.clientX - canvasContainer.left;
    let clickPositionOnCanvasY = event.clientY - canvasContainer.top;
    setOffset({
      x: squareSize / 2 - clickPositionOnCanvasX,
      y: squareSize / 2 - clickPositionOnCanvasY
    });
    setZoom(zoom * 2);*/
  };

  if (isZoomed) {
    return (
      <Box
        style={{
          zIndex: "1200",
          width: "100vw",
          height: "100vh",
          position: "fixed",
          top: 0,
          left: 0,
          backgroundColor: colors.black87,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CloseIcon
          closeFunction={() => {
            setIsZoomed(false);
            setSquareSize(350);
          }}
          darkMode={true}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
          }}
        />
        <canvas
          id="canvas"
          style={{ cursor: "zoom-out" }}
          onClick={() => {
            setIsZoomed(false);
            setSquareSize(350);
          }}
          width={imgSize.width * scale}
          height={imgSize.height * scale}
        />
      </Box>
    );
  }

  let [latestValue, mostRecentEvent] = getLatestValue(focusElement);
  latestValue = null;
  if (mostRecentEvent.attributes) {
    latestValue = mostRecentEvent.attributes.filter((attr) => attr.id === 140);
    if (latestValue.length > 0) {
      latestValue = latestValue[0].value;
    } else {
      latestValue = null;
    }
  }
  if ([76, 78].includes(get(mostRecentEvent, ["type", "id"]))) {
    latestValue = "confirmed";
  }

  return (
    <>
      <Grid item style={{ height: "25px" }}>
        <Grid container>
          {focusElement.roiOverlay && (
            <Grid item xs={3}>
              <span
                onClick={() => setShowRoi(!showRoi)}
                className={classes.showRoi}
              >
                {showRoi ? I18n.get("site_hideRoi") : I18n.get("site_showRoi")}
              </span>
            </Grid>
          )}
          <Grid
            item
            xs
            className="text-87 bold-500 text-center"
            style={{ marginBottom: "5px" }}
          >
            {latestValue && (
              <span style={{ marginRight: "10px", color: colors.green }}>
                {latestValue === 84 ? "OCR" : latestValue === 85 ? "LPR" : ""}
              </span>
            )}
            <span
              style={{
                textDecoration:
                  latestValue && latestValue !== "confirmed"
                    ? "line-through"
                    : "",
                color: latestValue === "confirmed" ? colors.green : "",
              }}
            >
              {focusElement.type
                ? I18n.get(`site_${focusElement.type.toLowerCase()}`)
                : ""}
            </span>
          </Grid>
          {focusElement.roiOverlay && <Grid item xs={3} />}
        </Grid>
      </Grid>
      <Grid
        item
        id="canvasContainer"
        style={{
          border: `1px solid ${colors.black542}`,
          borderRadius: "2px",
          height: `${squareSize + 2}px`,
          width: `${squareSize + 2}px`,
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <canvas
          id="canvas"
          style={{ cursor: "zoom-in" }}
          onClick={(e) => onZoom(e)}
          width={imgSize.width * scale}
          height={imgSize.height * scale}
        />
      </Grid>
    </>
  );
};

FocusPhoto.propTypes = {
  focusElement: PropTypes.object.isRequired,
};

export default withStyles(styles)(FocusPhoto);
